import AccountBalance from '@mui/icons-material/AccountBalance';
import BarChartIcon from '@mui/icons-material/BarChart';
import Description from '@mui/icons-material/Description';
import Warning from '@mui/icons-material/Warning';
import { Grid } from '@mui/material';
import { getCessionsSummary } from 'lib/api/business';
import TableCard from 'lib/designSystem/Card/TableCard';
import BarChart from 'lib/designSystem/Charts/BarChart';
import LineChart from 'lib/designSystem/Charts/LineChart';
import StatCard from 'lib/designSystem/Charts/StatCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SubtitleWithTooltip from 'lib/designSystem/SubtitleWithTooltip';
import EnhancedTable from 'lib/designSystem/Table';
import { BASE_COLORS } from 'lib/functions/const/colors';
import { formatMoney } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import DetailsFilter from './Components/DetailsFilter';
import useChart from './utils/useChart';
import useData from './utils/useData';

const CompanyFactoring: React.FC = () => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const { data: cessionsData, isPending } = getCessionsSummary(dispatch, id);
  const [months, setMonths] = useState<string[]>(['Últimos 24M']);
  const [selected, setSelected] = useState<string[]>([]);
  const { factoringData, payersData, metrics } = useData(months, selected);
  const { lineChartData, barChartData } = useChart(selected);

  if (isPending) return <CenteredCircularProgress />;

  return (
    <Grid container spacing={2} my={2}>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={9}>
          <TableCard>
            <SubtitleWithTooltip
              title="Cesiones"
              tooltipContent="Información de los últimos 24 meses"
              tooltipPlacement="right"
              variant="compact"
            >
              <DetailsFilter
                months={months}
                setMonths={setMonths}
                dataPayers={cessionsData?.dataPayers}
              />
            </SubtitleWithTooltip>
            <LineChart data={lineChartData} height={200} showArea={false} />
          </TableCard>
        </Grid>
        <Grid item xs={3} container spacing={2}>
          <Grid item xs={12}>
            <StatCard
              label="Promedio factorizado"
              value={formatMoney(metrics?.average || 0)}
              valueLabel="CLP"
              color={BASE_COLORS.blue}
              icon={<BarChartIcon sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12}>
            <StatCard
              label="Monto factorizado"
              value={formatMoney(metrics?.totalAmount || 0)}
              valueLabel="CLP"
              color={BASE_COLORS.green}
              icon={<AccountBalance sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12}>
            <StatCard
              label="Monto reclamado"
              value={formatMoney(metrics?.totalCancelAmount || 0)}
              valueLabel={'CLP ' + '(' + metrics?.cancelPercentage + '%)'}
              color={BASE_COLORS.orange}
              icon={<Warning sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12}>
            <StatCard
              label="Monto notas de crédito"
              value={formatMoney(metrics?.totalCreditNoteAmount || 0)}
              valueLabel={'CLP ' + '(' + metrics?.creditNotePercentage + '%)'}
              color={BASE_COLORS.amber}
              icon={<Description sx={{ fontSize: 24 }} />}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <TableCard>
          <SubtitleWithTooltip
            title="Por deudor"
            tooltipContent="Desglose de factorización por deudor"
            tooltipPlacement="right"
            variant="compact"
          />
          <EnhancedTable
            finalSumRow
            exportToFile="xlsx"
            tableSize="small"
            dataHeaders={[
              { key: 'name', label: 'Razon Social' },
              { key: 'identifier', label: 'Rut' },
              { key: 'amount', label: 'Monto', type: 'money' },
              {
                key: 'cancelPercentage',
                label: 'Reclamado',
                type: 'percentage',
              },
              { key: 'cancelAmount', label: 'Monto reclamado', type: 'money' },
              {
                key: 'creditNotePercentage',
                label: 'Notas de crédito',
                type: 'percentage',
              },
              {
                key: 'creditNoteAmount',
                label: 'Monto notas de crédito',
                type: 'money',
              },
            ]}
            data={payersData || []}
            selected={selected}
            setSelected={setSelected}
          />
        </TableCard>
      </Grid>

      <Grid item xs={12}>
        <TableCard>
          <SubtitleWithTooltip
            title="Por factoring"
            tooltipContent="Desglose de factorización por factoring"
            tooltipPlacement="right"
            variant="compact"
          />
          <EnhancedTable
            finalSumRow
            exportToFile="xlsx"
            tableSize="small"
            dataHeaders={[
              { key: 'name', label: 'Razon Social' },
              { key: 'identifier', label: 'Rut' },
              { key: 'amount', label: 'Monto', type: 'money' },
              {
                key: 'cancelPercentage',
                label: 'Reclamado',
                type: 'percentage',
              },
              { key: 'cancelAmount', label: 'Monto reclamado', type: 'money' },
              {
                key: 'creditNotePercentage',
                label: 'Notas de crédito',
                type: 'percentage',
              },
              {
                key: 'creditNoteAmount',
                label: 'Monto notas de crédito',
                type: 'money',
              },
            ]}
            data={factoringData || []}
          />
        </TableCard>
      </Grid>

      <Grid item xs={12}>
        <TableCard>
          <SubtitleWithTooltip
            title="Montos cedidos a factoring"
            tooltipContent="Información de los últimos 24 meses. Presiona un factoring para ver el desglose de sus cesiones por pagador"
            tooltipPlacement="right"
            variant="compact"
          />
          <BarChart
            data={barChartData}
            height={400}
            direction="horizontal"
            stacked={true}
            showLegend={false}
          />
        </TableCard>
      </Grid>
    </Grid>
  );
};

export default CompanyFactoring;
